import { createContext, useContext, type RefObject } from "react";
import type { StepDisplayState } from "../../../api/threads/liveMessages";
import type { ThreadOverview, TurnExecutor, TurnState } from "../../../api/threads/threadDecoders";
import type { ToolApproval } from "../entries/toolApproval";
import type { TurnProgressMap, WorkProgress } from "../live/turnProgress";

/** The open turn, from the live state (or the overview before it). */
export interface OpenTurnView {
  readonly turnId: string;
  /** `null` while the server writes the turn's record. */
  readonly state: TurnState | null;
  /** Who runs it: the agent, or a tool (no agent works on a tool step). */
  readonly executor: TurnExecutor;
  readonly stepKey: string | null;
  /** When it was requested: what its elapsed time counts from. */
  readonly requestedAt: string;
  /** A tool's latest count of its work, from the live progress; `null` before or without one. */
  readonly work: WorkProgress | null;
  /** The bytes of the local model fetched for the tool's run so far, before its work starts (V-4); `null` without a fetch. */
  readonly modelBytes: number | null;
  /** What it shows, as module C decides by its one turn rule (WD-4 L-2a, D2-6): an approval, the host offline, a slot, queued or running. */
  readonly displayState: StepDisplayState;
  /** The host's PC has had no free slot for it since then (X-2); `null` while it doesn't wait for one. */
  readonly waitingForSlotSince: string | null;
}

/** Every tool turn waiting for approval, including an agent's `run_tool` calls, by turn ID (TA-6, RL-1). */
export type RunApprovals = ReadonlyMap<string, ToolApproval>;

/** What every entry of a shown Thread needs to know about it and about the viewer. */
export interface ThreadContextValue {
  readonly threadId: string;
  readonly overview: ThreadOverview;
  readonly nameOf: (userId: string) => string;
  readonly currentUserId: string | null;
  readonly viewerIsHost: boolean;
  /** The entry the address names (`#12`), highlighted. */
  readonly highlightedEntry: number | null;
  /** The host offer the address names (the link the host sent): its panel is open (OW-09). */
  readonly addressedHostOffer: string | null;
  /** Fork at an entry; `null` when the viewer may not fork in this project. */
  readonly onForkAt: ((entryNumber: number) => void) | null;
  /** The human step waiting for an answer. */
  readonly openHumanStepEntry: number | null;
  /**
   * The request the current step shows with its inputs, where it is answered (F104): its
   * entry in the log doesn't show them a second time. `null` while the step card shows none.
   */
  readonly requestInStep: number | null;
  /** Human step requests a newer output superseded: asked again, answers settle nothing. */
  readonly supersededRequests: ReadonlySet<number>;
  readonly openTurn: OpenTurnView | null;
  /** The turns open now, as module C says (W-22): the open turn and its agent's tool runs, each with C's display state (D2-6). */
  readonly openRuns: ReadonlyMap<string, StepDisplayState>;
  /** Each open turn's live progress, by turn ID: what a tool block's own line shows while its run works (R3-2). */
  readonly turnProgress: TurnProgressMap;
  readonly runApprovals: RunApprovals;
  /** A step's name from the Thread's Workflow; the key while it isn't known. */
  readonly stepName: (stepKey: string | null, step?: string | null) => string;
  /** The Workflow's name, once read; `null` before or without one. */
  readonly workflowName: string | null;
  /** A Workflow version's name among the Thread's content version's Workflows, by item; `null` while unknown (F126). */
  readonly workflowNameOf: (reference: string) => string | null;
  /** A step's name by its key in one of the Workflows the Thread follows or followed (F126); `null` while unknown. */
  readonly stepNameIn: (workflow: string, stepKey: string) => string | null;
  /** The current step's section: what "Answer in the current step" brings into view (OW-08). */
  readonly currentStep: RefObject<HTMLElement | null>;
}

export const ThreadContext = createContext<ThreadContextValue | null>(null);

export function useThreadContext(): ThreadContextValue {
  const value = useContext(ThreadContext);
  if (value === null) {
    throw new Error("useThreadContext must be used inside a Thread view.");
  }
  return value;
}
